import 'utils/monitor';
import { OktaAuth } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import axios, { AxiosError } from 'axios';
import React from 'react';
import { QueryClient } from '@tanstack/react-query';
import { Route, Switch } from 'react-router-dom';

import dayjs from 'dayjs';
import IsBetween from 'dayjs/plugin/isBetween';
import IsSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import IsSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import Utc from 'dayjs/plugin/utc';

import { StyledEngineProvider } from '@mui/material/styles';

import Dashboard from 'views/dashboard/dashboard';
import OktaLoginCallback from 'views/dashboard/components/okta-login-callback';

import '@formatjs/intl-numberformat/locale-data/en'; // locale-data for en
// import '@formatjs/intl-numberformat/locale-data/es'; // locale-data for es
import '@formatjs/intl-numberformat/locale-data/es-CL';
import '@formatjs/intl-numberformat/locale-data/nb'; // locale-data for nb
import '@formatjs/intl-numberformat/polyfill';

import '@formatjs/intl-datetimeformat/add-all-tz'; // Add ALL tz data
import '@formatjs/intl-datetimeformat/locale-data/en'; // locale-data for en
// import '@formatjs/intl-datetimeformat/locale-data/es'; // locale-data for es
import '@formatjs/intl-datetimeformat/locale-data/es-CL';
import '@formatjs/intl-datetimeformat/locale-data/nb'; // locale-data for nb
import '@formatjs/intl-datetimeformat/polyfill';

import ProviderWrapper from 'contexts/provider-wrapper';
import googleTagManager from 'utils/google-tag-manager';
import { initializeDatadogRum, addDatadogRumError } from 'utils/datadog-rum';
import AppErrorBoundary from 'contexts/error-handler/app-error-boundary';

const oktaAuth = new OktaAuth({
  issuer: `${import.meta.env.VITE_OKTA_ISSUER}`,
  clientId: `${import.meta.env.VITE_OKTA_CLIENT_ID}`,
  redirectUri: `${import.meta.env.VITE_OKTA_REDIRECT_URI}/implicit/callback`,
  // offline_access returns the refresh token that keeps the session alive without re-prompting
  scopes: ['openid', 'email', 'profile', 'offline_access'],
  pkce: true,
  tokenManager: {
    storage: 'sessionStorage',
    autoRenew: true,
    syncStorage: true,
  },
});

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

// #region Initialize DayJS plugins

dayjs.extend(LocalizedFormat);
dayjs.extend(IsSameOrAfter);
dayjs.extend(IsSameOrBefore);
dayjs.extend(IsBetween);
dayjs.extend(Utc);

// #endregion Initialize DayJS plugins

axios.defaults.baseURL = `${import.meta.env.VITE_REST_API_URL}`;

axios.interceptors.request.use(
  function (config) {
    // Read the token per request so a rotated token is never stale on the header
    const accessToken = oktaAuth.getAccessToken();
    if (accessToken) config.headers.authorization = 'Bearer '.concat(accessToken);

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);
// Add a response interceptor
axios.interceptors.response.use(
  function (response) {
    // Do something with response data
    return response;
  },
  function (error: AxiosError) {
    // Do something with response error
    addDatadogRumError(error, {
      source: 'axios-response',
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
    });
    return Promise.reject(error);
  }
);

googleTagManager.initialize(import.meta.env.VITE_GOOGLE_TAG_MANAGER);
initializeDatadogRum();

const App: React.FC = () => {
  return (
    <StyledEngineProvider injectFirst>
      <Security
        oktaAuth={oktaAuth}
        restoreOriginalUri={(_, originalUri) => {
          if (originalUri) window.location.href = originalUri;
          else window.location.href = '/';
          // console.log(toRelativeUrl(originalUri, window.location.origin));
          // history.push(toRelativeUrl(originalUri, window.location.origin));
        }}>
        <ProviderWrapper queryClient={queryClient}>
          <Switch>
            <Route path="/implicit/callback" component={OktaLoginCallback} />
            <Route path="/" component={Dashboard} />
          </Switch>
        </ProviderWrapper>
      </Security>
      <div id="consent_blackbar"></div>
    </StyledEngineProvider>
  );
};

export default App;
